import { useContext, useMemo } from 'react'

import { Helmet } from 'react-helmet-async'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHome, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, Tools,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import ListBlankState from '@components/ListBlankState'
import PageHeader from '@components/PageHeader'

import { confirmDelete } from '@functions/entity'
import { groupEntitiesByEdition } from '@functions/publicationEdition'
import { sanitiseObject } from '@functions/utility'

import usePublicationBookingItem from '@hooks/usePublicationBookingItem'
import usePublicationBookingItems from '@hooks/usePublicationBookingItems'
import useSelector from '@hooks/useSelector'

import type { PublicationBookingItemFilters } from '@hooks/usePublicationBookingItems'

import PublicationEditionGroup from '../PublicationEditionGroup'

const allowedFilters = [] as string[]

interface PageContentProps {
  pageFilters: PublicationBookingItemFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      deselectAll,
    },
    hasSelectedEntities,
    selectedEntityIds,
  } = pageContext

  const sanitisedFilters = sanitiseObject(pageFilters, allowedFilters)

  const publicationBookingItemsPayload = usePublicationBookingItems({
    filters: {
      ...sanitisedFilters,
      removed: 'true',
    },
    performHttpRequests: true,
    requestOptions: {
      include_order_agency: true,
      include_order_campaign: true,
      include_order_publication: true,
      include_publication_booking_item_booking: true,
      include_publication_booking_item_order: true,
      include_publication_booking_publication_edition: true,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredPublicationBookingItems,
    filteredPublicationBookingItemsCount,
    loading,
  } = publicationBookingItemsPayload

  const publicationBookingItemPayload = usePublicationBookingItem()
  const {
    callbacks: {
      bulkDeletePublicationBookingItems: deleteFn,
    },
    urls: { cancelledItemsUrl },
  } = publicationBookingItemPayload

  const { orders, publicationBookings, publicationEditions } = useSelector(reduxState => reduxState.entities)

  const groupedPublicationBookingItemsByEdition = useMemo(() => groupEntitiesByEdition({
    callbacks: {
      getPrimaryGroupId: ({ previous_booking_id }) => publicationBookings[previous_booking_id]?.publication_edition_id,
      getSecondaryGroupId: ({ order_id }) => orders[order_id]?.agency_id,
    },
    entities: filteredPublicationBookingItems,
    publicationEditions,
    secondaryGroupKey: 'agencies',
  }), [filteredPublicationBookingItems.length])

  const hasGroupedPublicationBookingItemsByEdition = !!groupedPublicationBookingItemsByEdition.length

  const confirmDeletePayload = {
    callbacks: {
      afterAction: deselectAll,
      deleteFn,
    },
    options: {
      entityParams: selectedEntityIds,
      isBulkDelete: true,
    },
    strings: {
      confirmString: 'Are you sure? Please confirm with the publication first.',
      entityName: 'Booking Items',
    },
  }

  return (
    <>
      <Helmet>
        <title>Cancelled Items | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="cancelledItems"
        nestedNavigation
        tabBarItems={[
          {
            href: cancelledItemsUrl,
            icon: faHome,
            key: 'cancelledItems',
            title: 'CANCELLED ITEMS',
          },
        ]}
        title="Cancelled Items"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasGroupedPublicationBookingItemsByEdition && <ListBlankState />}

            {!loading && hasGroupedPublicationBookingItemsByEdition && (
              Object.values(groupedPublicationBookingItemsByEdition).map(group => (
                <PublicationEditionGroup
                  key={group.id}
                  agencyGroups={group.agencies}
                  orderCount={group.entityCount}
                  publicationEditionId={group.id}
                />
              ))
            )}

            {!loading && (
              <Box variant="white">
                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredPublicationBookingItemsCount}
                  loading={loading}
                  showBorderTop={false}
                />
              </Box>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <Tools>
              <Button
                buttonStyle="secondaryUtility"
                disabled={!hasSelectedEntities}
                loading={loading}
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                // @ts-expect-error TS2345
                onClick={() => confirmDelete(confirmDeletePayload)}
                size="medium"
              >
                Force Delete
              </Button>
            </Tools>
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
